import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { Ban, CheckCircle2, ChevronDown, ChevronUp, CircleCheck, CircleX, Loader2, Square, X, XCircle } from 'lucide-react';
import '../../automationScripts/styles/RunProgressBar.css';

/**
 * The batch's bottom bar — the single-run `RunProgressBar`, script-level.
 *
 * Same classes, same floating percentage bubble, same Details / Cancel / Close
 * actions, so a batch reads exactly like a single run. It is its own component
 * rather than a `RunProgressBar` fed a fake run: that one reads a single run's
 * `testResults`, and a batch has members, not permutations of one script.
 *
 * Deliberately no ETA and no lane count — nothing reports either.
 */
const BatchProgressBar = ({
  running, cancelled, hasFailures, complete, progressed, scripts, passed, failed,
  isDetailsOpen, onToggleDetails, onCancel, cancelDisabled, isCancelling, onClose,
  autoCloseIn, onKeepOpen,
}) => {
  const trackRef = useRef(null);
  const bubbleRef = useRef(null);
  const rafRef = useRef(null);

  // Same rule as a cancelled single run: the bar stops where the work got to.
  // A cancelled script is terminal but did not finish, so it never moves the
  // bar — cancelling cannot jump it to 100%.
  const pct = scripts > 0 ? Math.round((progressed / scripts) * 100) : 0;
  const pctRef = useRef(pct);
  pctRef.current = pct;

  // Repositions the portal bubble every frame so it stays on the fill's edge
  useEffect(() => {
    const tick = () => {
      if (trackRef.current && bubbleRef.current) {
        const rect = trackRef.current.getBoundingClientRect();
        bubbleRef.current.style.left = `${rect.left + (rect.width * pctRef.current) / 100}px`;
        bubbleRef.current.style.top = `${rect.top}px`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, []);

  const current = Math.min(complete + 1, scripts);
  const label = running
    ? `Running · Script ${current} of ${scripts}`
    : cancelled
      ? 'Cancelled'
      : `Done — ${passed} passed, ${failed} failed`;

  const bubbleState = running
    ? ''
    : cancelled
      ? ' mock-run-progress__bubble--cancelled'
      : hasFailures ? ' mock-run-progress__bubble--failed' : ' mock-run-progress__bubble--completed';

  return (
    <div className="mock-run-progress__inner">
      <div className="mock-run-progress__status" role="status" aria-label="Batch status">
        {running ? (
          <Loader2 size={14} className="mock-run-progress__status-icon mock-run-progress__status-icon--spinning" />
        ) : cancelled ? (
          <Ban size={14} className="mock-run-progress__status-icon mock-run-progress__status-icon--cancelled" />
        ) : hasFailures ? (
          <XCircle size={14} className="mock-run-progress__status-icon mock-run-progress__status-icon--failed" />
        ) : (
          <CheckCircle2 size={14} className="mock-run-progress__status-icon mock-run-progress__status-icon--completed" />
        )}
        <span>{label}</span>
      </div>

      <div className="mock-run-progress__bar-wrap">
        <div className="mock-run-progress__track" ref={trackRef}>
          <div className="mock-run-progress__fill" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {ReactDOM.createPortal(
        <span ref={bubbleRef} className={`mock-run-progress__bubble${bubbleState}`}>{pct}%</span>,
        document.body
      )}

      {/* A cancelled batch's tally reads as a verdict on work that never finished */}
      {!cancelled && (
        <div className="mock-run-progress__counts">
          <span className="mock-run-progress__badge mock-run-progress__badge--passed" title="Passed permutations">
            <CircleCheck size={10} />
            {passed}
          </span>
          <span className="mock-run-progress__badge mock-run-progress__badge--failed" title="Failed permutations">
            <CircleX size={10} />
            {failed}
          </span>
        </div>
      )}

      <div className="mock-run-progress__actions">
        <button
          type="button"
          className={`mock-run-progress__view-btn${isDetailsOpen ? ' mock-run-progress__view-btn--active' : ''}`}
          onClick={onToggleDetails}
          aria-expanded={isDetailsOpen}
        >
          {isDetailsOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          <span>{isDetailsOpen ? 'Hide' : 'Details'}</span>
        </button>
        {running && (
          <button
            type="button"
            className="mock-run-progress__cancel-btn"
            onClick={onCancel}
            disabled={cancelDisabled || isCancelling}
            title={isCancelling ? 'Cancelling…' : 'Cancel this batch'}
          >
            {/* Cooperative, like a single run: it says Cancelling until the
                scripts actually report that they stopped */}
            {isCancelling
              ? <><Loader2 size={11} className="mock-run-progress__cancel-spin" /><span>Cancelling…</span></>
              : <><Square size={10} /><span>Cancel</span></>}
          </button>
        )}
        {!running && autoCloseIn != null && (
          <span className="mock-run-progress__autoclose" role="timer" aria-live="polite">
            Closing in {autoCloseIn}s
            <button type="button" className="mock-run-progress__keep-open" onClick={onKeepOpen}>
              Keep open
            </button>
          </span>
        )}
        {/* Always closable, so a window can never get stuck on screen. While
            running, closing only hides it — the batch carries on. */}
        <button
          type="button"
          className="mock-run-progress__close-btn"
          onClick={onClose}
          title={running ? 'Close — the run keeps going' : 'Close'}
          aria-label="Close"
        >
          <X size={12} />
        </button>
      </div>
    </div>
  );
};

BatchProgressBar.propTypes = {
  running: PropTypes.bool.isRequired,
  cancelled: PropTypes.bool,
  hasFailures: PropTypes.bool,
  /** Scripts that have ended in any way — drives "Script N of M". */
  complete: PropTypes.number.isRequired,
  /** Scripts that ended by finishing, not by being cancelled — drives the bar. */
  progressed: PropTypes.number.isRequired,
  scripts: PropTypes.number.isRequired,
  passed: PropTypes.number.isRequired,
  failed: PropTypes.number.isRequired,
  isDetailsOpen: PropTypes.bool.isRequired,
  onToggleDetails: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  cancelDisabled: PropTypes.bool,
  isCancelling: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  /** Seconds until the finished window closes itself; null when not counting. */
  autoCloseIn: PropTypes.number,
  onKeepOpen: PropTypes.func,
};

BatchProgressBar.defaultProps = {
  cancelled: false,
  hasFailures: false,
  cancelDisabled: false,
  isCancelling: false,
  autoCloseIn: null,
  onKeepOpen: null,
};

export default BatchProgressBar;
